import { useCallback, useMemo, useState } from 'react'

import {
  ComparisonPatch,
  EntityChange,
  LayerChange,
} from '@sketch-hq/sketch-web-renderer'
import { CanvasSelection } from '@sketch/canvas'

import { VersionComparison } from './useVersionComparison'
import {
  canSplitEntity,
  canSwapEntity,
  canSwapLayer,
  CompareMode,
  modePatch,
} from './utils'

interface CompareModeState {
  /** The mode that is on. */
  mode: CompareMode
  /** What the canvas is asked to draw of the other version, or null. */
  patch: ComparisonPatch | null
  /** What the comparison says about the selected layer, frame or Section. */
  selectedChange: LayerChange | EntityChange | null
  /**
   * Whether Swap and Overlay are offered: the selection draws differently in
   * the other version - see `canSwapLayer` - and Inspect is off.
   */
  canSwap: boolean
  /** Turns a mode on, or off again if it is the one already on. */
  toggleMode(mode: CompareMode): void
}

const layerChangeOf = (
  compare: VersionComparison | undefined,
  layerPathHash: string | undefined
) =>
  layerPathHash
    ? (compare?.layers?.find(layer => layer.layerPathHash === layerPathHash) ??
      null)
    : null

/**
 * The compare mode the reader turned on, and what the canvas needs to draw it.
 * It lives up here because the buttons that set it and the patch list that
 * reads it are in different parts of the tree.
 *
 * Only one mode can be on, and it only applies to the thing it was turned on
 * for. It turns itself off when the selection moves, when a different version
 * is compared, or when the mode stops being available.
 *
 * None is offered while inspecting. The inspector reads the version on screen,
 * so its values would describe one version over the other one's pixels.
 */
export function useCompareMode(
  compare: VersionComparison | undefined,
  selection: CanvasSelection | null,
  isInspecting: boolean
): CompareModeState {
  const [mode, setMode] = useState<CompareMode>('none')

  const layerPathHash =
    selection?.kind === 'layer' ? selection.layerPathHash : undefined

  const selectedLayerChange = useMemo(
    () => layerChangeOf(compare, layerPathHash),
    [compare, layerPathHash]
  )

  const selectedEntityChange =
    selection?.kind !== 'layer' && selection?.uuid
      ? (compare?.entityByUUID?.get(selection.uuid) ?? null)
      : null

  const selectedChange = selectedLayerChange ?? selectedEntityChange

  const canSwap =
    !isInspecting &&
    (selectedLayerChange
      ? canSwapLayer(selectedLayerChange)
      : selectedEntityChange !== null &&
        canSwapEntity(selectedEntityChange, compare?.layers ?? []))

  /*
  A mode applies to one selected thing compared against one pair of versions.
  If either of those changes the mode no longer means anything, so it is turned
  off: a swap of version 3 says nothing about version 7.

  Both are read as ids rather than as the objects holding them. A layer by its
  path hash and not its uuid, because every instance of a symbol has the same
  uuid, and moving between two instances is a new selection. A frame or Section
  by its kind as well, as nothing promises the two never share a uuid. The
  versions by the comparison's own `pairId`, for the reason given where it is
  built.
  */
  const selected =
    layerPathHash ?? (selection ? `${selection.kind}:${selection.uuid}` : null)
  const pair = compare?.pairId ?? null

  const [applied, setApplied] = useState({ selected, pair })
  const movedOn = applied.selected !== selected || applied.pair !== pair

  // The other reason to turn a mode off: a mode is hidden when it would show
  // nothing of the selection, and a hidden button cannot turn it off
  const canSplit =
    !isInspecting &&
    selectedEntityChange !== null &&
    canSplitEntity(selectedEntityChange, compare?.layers ?? [])
  // An overlay of the same pixels shows nothing either, so it follows the swap
  const stillOffered =
    (mode !== 'split' || canSplit) &&
    ((mode !== 'swap' && mode !== 'overlay') || canSwap)

  // Set while rendering rather than in an effect. An effect would draw the old
  // mode for a frame first, and a click in that frame would act on it.
  if (movedOn) setApplied({ selected, pair })
  if (movedOn || !stillOffered) setMode('none')

  const toggleMode = useCallback(
    (mode: CompareMode) =>
      setMode(current => (current === mode ? 'none' : mode)),
    []
  )

  // Memoised so the canvas keeps reconciling against the same object
  const patch = useMemo(
    () => modePatch(selection, selectedChange, mode),
    [selection, selectedChange, mode]
  )

  return { mode, patch, selectedChange, canSwap, toggleMode }
}
